<template>
  <div class="step-card">
    <div class="step-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">What is the first thing Eddy can help with?</h2>
        <p class="step-subtitle">Choose where you'd like to start</p>
      </div>

      <!-- Options Grid -->
      <div class="options-grid">
        <button
          v-for="option in helpOptions"
          :key="option.id"
          class="option-card"
          :class="{ active: selectedOption === option.id }"
          :aria-pressed="selectedOption === option.id"
          :style="
            selectedOption === option.id
              ? {
                  borderColor: option.activeBorder,
                  backgroundColor: option.activeBg,
                }
              : {}
          "
          @click="handleOptionSelect(option.id)"
        >
          <div class="option-content">
            <div
              class="icon-container"
              :style="{ backgroundColor: option.iconBg }"
              aria-hidden="true"
            >
              <div class="icon-wrapper">
                <component
                  :is="option.icon"
                  width="32"
                  height="32"
                  :color="option.iconColor ? option.iconColor : undefined"
                />
              </div>
            </div>
            <div class="text-container">
              <p class="option-title">{{ option.title }}</p>
              <p class="option-description">{{ option.description }}</p>
            </div>
          </div>
        </button>
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <ActionButton @click="handleBack" variant="outline" class="back-button"
          >Back</ActionButton
        >
        <div class="right-buttons">
          <ActionButton
            @click="handleGoToEddy"
            variant="text"
            class="goto-button"
            >Go to Eddy</ActionButton
          >
          <ActionButton
            :disabled="!selectedOption"
            @click="handleNext"
            class="next-button"
            >Continue
          </ActionButton>
        </div>
      </div>
    </div>

    <!-- Dynamic Confirmation Modal -->
    <DynamicConfirmationModal
      v-model="showModal"
      title="Want to Skip this Part?"
      message="You can explore more in the eddy landing page. See you on Eddy!"
      cancel-text="Back"
      confirm-text="Continue to Eddy"
      icon="/images/icon/loading-icon.svg"
      @confirm="handleConfirmGoToEddy"
      @cancel="handleCancelGoToEddy"
    />
  </div>
  <ActionButton
    @click="handleGoToEddy"
    variant="text"
    class="goto-button-mobile"
    >Go to Eddy</ActionButton
  >
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, defineEmits, watch, computed } from "vue";
import { useRouter } from "vue-router";
import ActionButton from "../common/ActionButton.vue";
import DynamicConfirmationModal from "../common/DynamicConfirmationModal.vue";
import GridIcon from "../icons/GridIcon.vue";
import ViewIcon from "../icons/ViewIcon.vue";
import CreateIcon from "../icons/CreateIcon.vue";
import BookIcon from "../icons/BookIcon.vue";
import RefreshIcon from "../icons/RefreshIcon.vue";

const props = defineProps({
  modelValue: {
    type: Number,
    default: null,
  },
  role: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  "next",
  "back",
  "goto-eddy",
  "update:modelValue",
  "select-section",
]);

const baseOptions = ref([
  {
    id: 1,
    title: "Browse hands-on Makercart activities",
    description:
      "Browse our catalog of 100+ Makercart activities. Each includes lesson materials you can preview before deciding on your purchase.",
    iconBg: "#E6F4EF",
    icon: GridIcon,
    activeBorder: "#2DA479",
    activeBg: "#E6F4EF",
  },
  {
    id: 2,
    title: "View a Makercart activity in detail",
    description:
      "Take a closer look at one of our Makercart activities selected just for you.",
    iconBg: "#FCE6F0",
    icon: ViewIcon,
    activeBorder: "#FF1382",
    activeBg: "#FCE6F0",
  },
  {
    id: 3,
    title: "Create tech-infused lesson packages",
    description:
      "Create a unique lesson integrated with free technology tools that encourage digital making!",
    iconBg: "#EDEDFC",
    icon: CreateIcon,
    activeBorder: "#797AF4",
    activeBg: "#EDEDFC",
  },
]);

const trainerOption = ref({
  id: 4,
  title: "Go to Edm8ker Trainer Academy",
  description:
    "The Edm8ker Trainer Academy enables prospective trainers to complete modules that qualify you as a certified trainer, eligible for contract opportunities.",
  iconBg: "#EAF0F4",
  icon: BookIcon,
  activeBorder: "#00599A",
  activeBg: "#E6ECF0",
  iconColor: "#0478CE",
});

const helpOptions = computed(() => {
  if (props.role.toLowerCase() === "trainer") {
    return [trainerOption.value, ...baseOptions.value];
  }
  return baseOptions.value;
});

const router = useRouter();
const showModal = ref(false);

const selectedOption = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newValue) => {
    selectedOption.value = newValue;
  },
  { immediate: true }
);

const handleOptionSelect = (id) => {
  selectedOption.value = id;
  emit("update:modelValue", id);
};

const handleNext = () => {
  if (selectedOption.value) {
    emit("next", { selectedOption: selectedOption.value });
    emit("select-section", selectedOption.value);
  }
};

const handleBack = () => {
  emit("back");
};

const handleGoToEddy = () => {
  showModal.value = true;
};

const handleConfirmGoToEddy = () => {
  showModal.value = false;
  router.push({ name: "HomePage" });
};

const handleCancelGoToEddy = () => {
  showModal.value = false;
};
</script>

<style scoped>
.step-card {
  background-color: white;
  border: 1px solid #0478ce;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: flex-end;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #3b4648;
}

.step-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.step-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  width: 100%;
}

.option-card {
  border: 2px solid #d8dae5;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: white;
  text-align: left;
  min-width: 0;
  flex: 1 1 0;
  min-width: 300px;
}

.option-card:hover:not(.active) {
  border-color: #0478ce;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(4, 120, 206, 0.15);
}

.option-card.active {
  border-width: 2px;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.option-content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
}

.icon-container {
  border-radius: 8px;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.text-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
}

.option-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  letter-spacing: 0.014px;
  margin: 0;
  text-align: left;
}

.option-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #40444b;
  letter-spacing: 0.048px;
  margin: 0;
  text-align: left;
}

.button-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.back-button {
  background-color: white;
  border: 1px solid #bac1cc;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  min-width: 100px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
  color: #3b4648;
}

.goto-button {
  background-color: #f2f3fb;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.goto-button:hover {
  color: #3b4648;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.next-button {
  width: 100px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.goto-button-mobile {
  display: none;
}

@media (max-width: 768px) {
  .step-card {
    min-height: auto;
  }

  .options-grid {
    flex-direction: column;
  }

  .option-card {
    min-width: 100%;
  }

  .button-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .right-buttons {
    width: 100%;
  }

  .back-button {
    margin-bottom: 0px;
  }

  .goto-button {
    display: none;
  }

  .goto-button-mobile {
    margin-top: 10px;
    display: block;
  }

  .back-button,
  .goto-button,
  .next-button {
    flex: 1;
  }
}
</style>
